import { createStore } from 'zustand/vanilla';
import type { MediaLibraryData, MediaLibraryActions } from './types';
import { RequestStatus } from '@/shared/model/requestStatus';
import { getErrorMessage } from '@/shared/lib/errors';
import { mediaLibraryService } from '../api/mediaLibraryService';

/** Создаёт независимый каталог для страницы или окна выбора */
export const createMediaLibraryStore = () => createStore<MediaLibraryData & MediaLibraryActions>((
    set,
    get
) => {
    let foldersRequest: Promise<void> | undefined;
    let foldersRevision = 0;

    return {
        items: [],
        folders: [],
        allCount: 0,
        status: RequestStatus.Idle,
        error: null,
        revision: 0,
        foldersStatus: RequestStatus.Idle,
        foldersError: null,

        loadFolders: async (force = false) => {
            if (!force && get().foldersStatus === RequestStatus.Success) return;
            if (!force && foldersRequest) return foldersRequest;
            const revision = ++foldersRevision;

            set({
                foldersStatus: RequestStatus.Loading,
                foldersError: null
            });

            /** Загружает справочник папок независимо от страницы файлов */
            const request = async (): Promise<void> => {
                try {
                    const folders = await mediaLibraryService.folders();

                    if (revision !== foldersRevision) return;

                    set({
                        folders,
                        foldersStatus: RequestStatus.Success
                    });
                } catch (error: unknown) {
                    if (revision !== foldersRevision) return;

                    set({
                        foldersStatus: RequestStatus.Error,
                        foldersError: getErrorMessage(error, 'Не удалось загрузить папки')
                    });
                } finally {
                    if (revision === foldersRevision) foldersRequest = undefined;
                }
            };

            foldersRequest = request();

            return foldersRequest;
        },

        load: async query => {
            const revision = get().revision + 1;

            set({
                revision,
                status: RequestStatus.Loading,
                error: null
            });

            try {
                void get().loadFolders();
                const page = await mediaLibraryService.list(query);

                if (get().revision !== revision) return;

                set({
                    items: page.items ?? [],
                    allCount: page.allCount ?? 0,
                    status: RequestStatus.Success
                });
            } catch (error: unknown) {
                if (get().revision !== revision) return;

                set({
                    status: RequestStatus.Error,
                    error: getErrorMessage(error, 'Не удалось загрузить медиатеку')
                });
            }
        },
    };
});
